import React from 'react';
import PropTypes from 'prop-types';
import { Provider } from 'react-redux';
import {
  Redirect, Route, Router, Switch
} from 'react-router-dom';
// App Layout
import Login from 'views/login/login';
import SiteTenancy from 'views/login/site-tenancy';
import SiteSecurity from 'views/site/security/site-security';
import SiteLayout from 'views/site/layout/site-layout';
import NotFoundPage from 'components/404Page';
import NotAllowedPage from 'components/403Page';
// Home
import MyRoster from 'views/Roster/Calendar';
// Roster
import Roster from 'views/Roster/timeline/Roster';
import RosterPublish from 'views/Roster/timeline/RosterPublish';

import TemplateTimeline from 'views/Roster/timeline/TemplateTimeline';
// Travel
import {
  Itineraries, Legs, Locations, Routes
} from 'views/travel';
// Availability
import {
  Cycles as AvailabilityCycles, LeaveStatus, LeaveTypes, Legs as AvailabilityLegs, LegTypes
} from 'views/availability';
// Reports
import Reports from 'views/reports/reports';
// Manage
import { MyAccount, Users } from 'views/Users';
import {
  Actions, ActionTypes, Competencies, CompetencyTypes, Tags
} from 'views/manage';
import { OptimiserStatus } from 'views/OptimiserStatus';
import {
  Activity, ActivityTypes, RosterBuilders, Rosters, Templates, Validations
} from 'views/Roster';
// Locale
import { LocaleProvider } from 'antd';
import locale from 'antd/lib/locale-provider/en_GB';
// Authorization
import { Permissions } from 'components/Permissions';
import * as permissions from 'library/permissions';
import { isOptimiserActive } from 'library/optimiser';
import 'moment/locale/en-au';
import RosterBuilderTimeline from 'views/Roster/timeline/RosterBuilderTimeline';

// Optimiser-status page is gated on the Optimiser permission (CanViewOptimiser)
// AND the tenancy being Optimiser-active, so a permissioned user in a disabled
// tenancy can't reach the page by typing the URL (FR-6.2).
const OptimiserStatusGuarded = props => (
  isOptimiserActive() ? <OptimiserStatus {...props} /> : <NotAllowedPage {...props} />
);

const AppRouter = ({ store, history }) => (
  <Permissions.Provider value={{ permissions }}>
    <Provider store={store}>
      <LocaleProvider locale={locale}>
        <Router history={history}>
          <Switch>
            <Route exact path="/" render={() => <Redirect from="/" to="/login" />} />
            <Route path="/login" component={Login} />
            <Route path="/tenancy" component={SiteTenancy} />
            <SiteSecurity>
              <SiteLayout>
                <Route path="/home/account/" component={permissions.BasicAccess(MyAccount, NotAllowedPage)} />
                <Route path="/home/roster/" component={permissions.BasicAccess(MyRoster, NotAllowedPage)} />

                <Route path="/roster/list" component={permissions.CanViewRosters(Rosters, NotAllowedPage)} />
                <Route path="/roster/detail/:id" component={permissions.CanViewRosters(Roster, NotAllowedPage)} />
                <Route exact path="/roster/templates" component={permissions.CanViewActivities(Templates, NotAllowedPage)} />
                <Route path="/roster/templates/timeline/:id" component={permissions.CanViewActivities(TemplateTimeline, NotAllowedPage)} />
                <Route path="/roster/activities" component={permissions.CanViewActivities(Activity, NotAllowedPage)} />
                <Route path="/roster/activityTypes" component={permissions.CanAdminActivities(ActivityTypes, NotAllowedPage)} />
                <Route path="/roster/validations" component={permissions.CanAdminRosters(Validations, NotAllowedPage)} />
                <Route exact path="/roster/rosterBuilders" component={permissions.CanViewActivities(RosterBuilders, NotAllowedPage)} />
                <Route path="/roster/rosterBuilders/timeline/:id" component={permissions.CanViewActivities(RosterBuilderTimeline, NotAllowedPage)} />

                <Route path="/travel/itineraries" component={permissions.CanViewTravel(Itineraries, NotAllowedPage)} />
                <Route path="/travel/legs" component={permissions.CanViewTravel(Legs, NotAllowedPage)} />
                <Route path="/travel/routes" component={permissions.CanViewTravel(Routes, NotAllowedPage)} />
                <Route path="/travel/locations" component={permissions.CanViewTravel(Locations, NotAllowedPage)} />

                <Route path="/availability/cycles" component={permissions.CanViewAvailability(AvailabilityCycles, NotAllowedPage)} />
                <Route path="/availability/cycleLegs" component={permissions.CanViewAvailability(AvailabilityLegs, NotAllowedPage)} />
                <Route path="/availability/legs" component={permissions.CanViewAvailability(AvailabilityLegs, NotAllowedPage)} />
                <Route path="/availability/legTypes" component={permissions.CanViewAvailabilities(LegTypes, NotAllowedPage)} />
                <Route path="/availability/leaveTypes" component={permissions.CanViewAvailabilities(LeaveTypes, NotAllowedPage)} />
                <Route path="/availability/leaveStatus" component={permissions.CanViewAvailabilities(LeaveStatus, NotAllowedPage)} />

                <Route path="/reports/dashboard" component={permissions.CanViewReports(Reports, NotAllowedPage)} />

                <Route path="/manage/users" component={permissions.CanViewUsers(Users, NotAllowedPage)} />
                <Route path="/manage/competencies" component={permissions.CanViewCompetencies(Competencies, NotAllowedPage)} />
                <Route path="/manage/competencyTypes" component={permissions.CanViewCompetencies(CompetencyTypes, NotAllowedPage)} />
                <Route path="/home/actions" component={permissions.CanViewActions(Actions, NotAllowedPage)} />
                <Route path="/manage/actionTypes" component={permissions.CanViewActions(ActionTypes, NotAllowedPage)} />
                <Route path="/manage/tags" component={permissions.CanViewTags(Tags, NotAllowedPage)} />
                <Route path="/manage/optimiser-status" component={permissions.CanViewOptimiser(OptimiserStatusGuarded, NotAllowedPage)} />
                <Route exact path="/manage/optimiser-readiness" render={() => <Redirect to="/manage/optimiser-status" />} />

                <Route path="/roster/publish/:id" component={permissions.CanAdminRosters(RosterPublish, NotAllowedPage)} />

                <Route exact path="/404" component={NotFoundPage} />
                <Route path="/403" component={NotAllowedPage} />
                <Route to="/404" />
              </SiteLayout>
            </SiteSecurity>
          </Switch>
        </Router>
      </LocaleProvider>
    </Provider>
  </Permissions.Provider>
);

AppRouter.propTypes = {
  store: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

export default AppRouter;
